/**
 * Links one room's Yjs message list to the radio. Framework-free so it can be
 * exercised with real Yjs documents and a simulated airwave (see the
 * three-phone test); useLoRaRoomBridge just attaches it while a room is on
 * the radio.
 *
 *  - Authored on this phone  -> on air now (author + tag).
 *  - From an internet peer   -> offered to the gateway (staggered, suppressed).
 *  - Heard on air            -> into the room, unless its internet original is
 *                               already here; the tag tells the gateway to
 *                               stand down.
 *  - Radio copy + original   -> whichever arrives second is removed.
 */

import type * as Y from 'yjs'
import type { MessageEntry } from '../sync/crdt'
import type { SendResult } from './client'
import type { RoomRadioText } from './session'
import { RadioGateway, type GatewayClock } from './gateway'
import { entryFromRadio, formatForRadio, messageTag, parseFromRadio, taggedCopyId } from './room-text'

/** Transaction origin for anything that arrived over LoRa. */
export const LORA_ORIGIN = 'lora'

/** The slice of the radio session this needs. */
export interface RadioRoomPort {
  textBudget(): number
  sendRoomText(roomId: string, text: string): Promise<SendResult>
  onRoomText(roomId: string, cb: (m: RoomRadioText) => void): () => void
}

export interface RoomRadioLinkOptions {
  doc: Y.Doc
  messages: Y.Array<MessageEntry>
  roomId: string
  radio: RadioRoomPort
  /** Stable per-phone key (the radio's public key) for gateway staggering. */
  gatewayKey: string
  clock?: GatewayClock
  onError?: (e: unknown) => void
}

export function linkRoomToRadio(o: RoomRadioLinkOptions): () => void {
  const { doc, messages, roomId, radio } = o
  const report = o.onError ?? ((e: unknown) => console.error('[LoRaBridge]', e))

  const send = async (entry: MessageEntry) => {
    const text = formatForRadio(entry, radio.textBudget())
    if (text) await radio.sendRoomText(roomId, text)
  }

  const gateway = new RadioGateway({ gatewayKey: o.gatewayKey, clock: o.clock, forward: send })

  const removeById = (id: string) => {
    const idx = messages.toArray().findIndex((x) => x.id === id)
    if (idx >= 0) doc.transact(() => messages.delete(idx, 1), LORA_ORIGIN)
  }

  const hasOriginalFor = (tag: string) => messages.toArray().some((x) => !x.radioTag && messageTag(x.id) === tag)

  // ---- radio -> room ----
  const stopHearing = radio.onRoomText(roomId, (m) => {
    const { tag } = parseFromRadio(m.text)
    if (tag) {
      gateway.heard(tag)
      if (hasOriginalFor(tag)) return
    }
    const entry = entryFromRadio(roomId, m.text, m.senderTimestamp)
    if (messages.toArray().some((x) => x.id === entry.id)) return
    doc.transact(() => messages.push([entry]), LORA_ORIGIN)
  })

  // ---- room -> radio ----
  const observer = (event: Y.YArrayEvent<MessageEntry>, txn: Y.Transaction) => {
    if (txn.origin === LORA_ORIGIN) return
    const added: MessageEntry[] = []
    event.changes.added.forEach((item) => {
      for (const c of item.content.getContent() as MessageEntry[]) added.push(c)
    })
    for (const entry of added) {
      // One message, not two: drop the radio copy once the original is here.
      if (entry.radioTag) {
        if (hasOriginalFor(entry.radioTag)) queueMicrotask(() => removeById(entry.id))
      } else {
        const copyId = taggedCopyId(messageTag(entry.id))
        if (messages.toArray().some((x) => x.id === copyId)) queueMicrotask(() => removeById(copyId))
      }

      if (txn.local) {
        gateway.heard(messageTag(entry.id)) // authored here; nobody needs to forward it
        send(entry).catch(report)
      } else {
        gateway.offer(entry)
      }
    }
  }
  messages.observe(observer)

  return () => {
    messages.unobserve(observer)
    stopHearing()
    gateway.dispose()
  }
}
